/* sts-routine-shared.js — the half of the routine UI that two pages must share.
 *
 * Spec: docs/new-theme-v4/specs/05-skin-profile-core-spec.md §5 (work item W32).
 * The spec asked for four files (sts-routine-core / -selling-plans /
 * -routine-cart / -swap-drawer). They ship as four NAMESPACES inside one file
 * because the Skin Profile workstream owns exactly one shared asset path and
 * four <script> tags on a page that already has to stay under a first-screen
 * budget is four round trips for ~30KB. The seams are the same; splitting the
 * file later is a copy-paste, not a redesign.
 *
 *   window.STSRoutine.core   — pure step helpers
 *   window.STSRoutine.money  — presentment-aware currency formatting
 *   window.STSRoutine.plans  — selling plans / Subscribe & Save
 *   window.STSRoutine.cart   — cart writes + the theme's reward lines
 *   window.STSRoutine.drawer — swap-candidate list, notes and tags
 *
 * All five were moved out of assets/sts-liquid-quiz-results.js (pre-W32 lines
 * 129-330, 504-529, 840-1190, 1271-1429, 2707-3005).
 *
 * EVERY function here is stateless or owns its state explicitly (PlanStore),
 * because the results page keeps its state in one module-level `S` and the
 * Skin Profile keeps its own. Nothing in this file may read a page's globals.
 *
 * THE LIVE RESULTS PAGE RUNS ON THIS FILE. assets/sts-liquid-quiz-results.js
 * no longer carries its own copies of these helpers; it calls them here. A
 * change to anything below is a change to the production funnel (quiz ->
 * results -> checkout), so walk the results page before shipping one.
 * Where this file and the results page used to disagree, the results page's
 * behaviour was kept — see the notes on stepFieldsFromCandidate, money.set,
 * PlanStore's onChange, cartRoute and applyDiscountCodes.
 */
(function () {
  'use strict';

  var NS = (window.STSRoutine = window.STSRoutine || {});

  /* ================================================================== */
  /* net — every request in this file has a deadline                     */
  /* ================================================================== */

  /* This file is the cart path for THREE surfaces at once: the Skin Profile's
     "Add all"/"Add selected", the quiz results page's "Start my routine", and
     the personalize upsell button. Each of them sets its own busy state before
     calling in here and clears it in the tail of the chain that starts below.
     A fetch that hangs neither resolves nor rejects, so none of those tails ran
     and all three buttons sat disabled, reading "Adding…", for the rest of the
     visit — every `.catch` in this file was unreachable in exactly the case it
     was written for.
     A deadline converts a hang into the failure each caller already handles:
     the cart read falls back to "add everything", the add surfaces its error,
     and the settle poll gives up. */
  var NET_TIMEOUT_MS = 8000;

  function netFetch(url, opts, ms) {
    var limit = ms || NET_TIMEOUT_MS;
    var ctrl = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var o = {};
    var k;
    for (k in (opts || {})) { if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k]; }
    if (ctrl) o.signal = ctrl.signal;
    var timer = window.setTimeout(function () {
      if (ctrl) { try { ctrl.abort(); } catch (e) { /* already gone */ } }
    }, limit);
    /* Plain window.fetch on purpose: shop_events_listener wraps it, and that
       is the only source of product_added_to_cart. See layout/theme.liquid. */
    var req = window.fetch(url, o);
    /* No AbortController: the race still rejects on time, so the caller is
       released even though the socket stays open. */
    var race = ctrl ? req : Promise.race([req, new Promise(function (_, reject) {
      window.setTimeout(function () { reject(new Error('timeout')); }, limit);
    })]);
    return race.then(
      function (r) { window.clearTimeout(timer); return r; },
      function (e) {
        window.clearTimeout(timer);
        throw (e && e.name === 'AbortError') ? new Error('timeout') : e;
      }
    );
  }

  /* Exported: sts-profile.js carried a copy of it. One place to fix. */
  NS.net = netFetch;

  /* ================================================================== */
  /* core — pure step helpers                                            */
  /* ================================================================== */

  function isAddableStep(step) {
    return !!step.dtcVariantId && !!step.title &&
      step.priceMsrp !== null && step.priceMsrp !== undefined;
  }

  function isInformationalStep(step) {
    return !step.dtcVariantId || !step.title;
  }

  function isBundleAddableStep(step) {
    return isAddableStep(step) && step.inStock !== false;
  }

  /* An alternative the shopper can actually buy. The stock check is NOT
   * cosmetic: without it the swap drawer offers products that fail at
   * add-to-cart. */
  /* `slot` optional, skipped when either side omits it (the legacy adapter
     carries none). Where both know it, a cross-slot candidate is refused: a
     cleanser must never stand in for a sunscreen (D32). */
  function isValidSwapCandidate(candidate, slot) {
    return !!candidate && !!candidate.dtcVariantId && !!candidate.title &&
      candidate.inStock !== false &&
      (!slot || !candidate.slot || sameSlot(candidate.slot, slot));
  }

  /* The fields a candidate contributes when it takes a step's place. Shared by
   * applySwap (shopper chose it) and substituteOutOfStock (we chose it for
   * them) so the two can never drift.
   *
   * `compareAt` is deliberately NOT in this list: the live results page has
   * never carried it across a swap, and this file must not change the funnel.
   * Known consequence, on both pages: a swapped step keeps the ORIGINAL
   * product's compare-at, so the drawer's "Currently in your routine" card can
   * show that product's was-price / "% off" tag. Fix it here, once, for both
   * pages — as its own reviewed change. */
  function stepFieldsFromCandidate(candidate) {
    return {
      title: candidate.title,
      /* #1055: the name with the variant; never the swapped-out product's. */
      displayTitle: candidate.displayTitle || candidate.title,
      brand: candidate.brand,
      image: candidate.image,
      priceMsrp: candidate.priceMsrp,
      dtcVariantId: candidate.dtcVariantId,
      inStock: candidate.inStock,
      handle: candidate.handle,
      keyIngredients: candidate.keyIngredients || [],
      ingredientsSummary: null,
      url: null,
      /* The picker A/B's badges (assets/sts-ab.js): the new product's,
         or none - never the swapped-out product's. */
      badges: candidate.badges
    };
  }

  /* Pure: returns a NEW array; keeps slot/tier/time/swapCandidates. The
     shopper chose this one, so an out-of-stock stand-in's marks go with the
     product it replaces; a rollback (the step itself as candidate) keeps them. */
  function applySwap(steps, index, candidate) {
    if (index < 0 || index >= steps.length || !isValidSwapCandidate(candidate)) return steps;
    var sub = candidate.substituted === true;
    return steps.map(function (step, i) {
      if (i !== index) return step;
      return Object.assign({}, step, stepFieldsFromCandidate(candidate), {
        substituted: sub, substitutedFrom: sub ? candidate.substitutedFrom : null, oosOriginal: sub ? candidate.oosOriginal : null
      });
    });
  }

  function applySwaps(steps, swaps) {
    return Object.keys(swaps).reduce(function (acc, indexKey) {
      return applySwap(acc, Number(indexKey), swaps[indexKey]);
    }, steps);
  }

  /* Within-slot out-of-stock substitution, on EVERY surface that renders a
   * routine. 05-Q32 once said the Skin Profile must show a sold-out product
   * rather than replace it; Luis reversed that (D32) and the rule is now the
   * same everywhere - substitute, from the same slot, and name both products.
   * With assets/sts-step-fallback.js on the page (every routine surface loads
   * it) its standIn() decides, with these rules: approved only, the picker
   * A/B order, "Keep waiting for it", and the original kept to put back. */
  function substituteOutOfStock(step) {
    var F = window.STS && window.STS.stepFallback;
    if (F) return F.standIn(step, isValidSwapCandidate, stepFieldsFromCandidate);
    if (!step || step.inStock !== false || step.substituted === true) return step;
    var replacement = null;
    for (var i = 0; i < (step.swapCandidates || []).length; i++) {
      if (isValidSwapCandidate(step.swapCandidates[i], step.slot)) { replacement = step.swapCandidates[i]; break; }
    }
    if (!replacement) return step;
    return Object.assign({}, step, stepFieldsFromCandidate(replacement), {
      substituted: true,
      substitutedFrom: step.displayTitle || step.title || null
    });
  }

  /* v6 3.3: a unit's quantity (the Skin Profile stamps `qty`); totals and
     cart lines multiply by it. Absent = 1, so every other page is unchanged. */
  function qtyOf(u) { return Math.max(1, parseInt(u && u.qty, 10) || 1); }

  function computeAddableTotal(steps) {
    return steps.reduce(function (sum, step) {
      return isBundleAddableStep(step) ? sum + step.priceMsrp * qtyOf(step) : sum;
    }, 0);
  }

  /* --- step groups ------------------------------------------------- */

  function combineTime(times) {
    var hasBoth = times.some(function (t) { return t === 'BOTH'; });
    if (hasBoth) return 'BOTH';
    var hasAm = times.some(function (t) { return t === 'AM'; });
    var hasPm = times.some(function (t) { return t === 'PM'; });
    if (hasAm && hasPm) return 'BOTH';
    if (hasAm) return 'AM';
    if (hasPm) return 'PM';
    return null;
  }

  /* One product = one element. The same dtcVariantId ANYWHERE in the routine is
   * ONE group tagged AM + PM — which is what makes the AM+PM rule a data fact
   * rather than a rendering trick: totals, the cart and the subscription all
   * count the group once.
   *
   * The key is the variant id alone. It used to be the slot's CATEGORY plus the
   * variant id, which broke one bottle into two groups in two ways:
   *   - a product doing double duty (a treatment at night, a moisturizer in the
   *     morning) was two groups, while dedupeByVariant — the function the cart
   *     and every total go through — has always counted it as one bottle. The
   *     rendering and the charge disagreed;
   *   - the category is looked up from the slot family, so any family missing
   *     from the label table below fell back to the raw SLOT code, and then the
   *     SAME bottle in the morning CX21 slot and the evening CX22 slot was two
   *     groups, two "Add" rows and no AM + PM pill.
   * Variant id wins over category, so neither can happen. Informational steps
   * carry no variant, so they stay distinct by position. */
  function groupKeyFor(step, index) {
    if (isInformationalStep(step)) return 'info:' + index;
    return 'v:' + step.dtcVariantId;
  }

  function buildStepGroups(steps) {
    var order = [];
    var byKey = new Map();
    steps.forEach(function (step, index) {
      var key = groupKeyFor(step, index);
      var existing = byKey.get(key);
      if (existing) existing.push(index);
      else { byKey.set(key, [index]); order.push(key); }
    });
    return order.map(function (key) {
      var indices = byKey.get(key);
      return {
        key: key,
        indices: indices,
        primaryIndex: indices[0],
        time: combineTime(indices.map(function (i) { return steps[i].time; }))
      };
    });
  }

  /* THE product count. One element per physical bottle the shopper is actually
   * being offered, given the groups a surface has decided to show.
   *
   * It is the count that agrees with the money, because it is the same set
   * every total is summed over: buildStepGroups already keys on the variant, so
   * a twice-daily bottle is one element, and isBundleAddableStep drops the two
   * kinds of group that carry no price — an INFORMATIONAL step (a slot the
   * routine has nothing in) and an OUT-OF-STOCK one (shown, deliberately, but
   * not on sale).
   *
   * This exists because the two surfaces disagreed. The Skin Profile counted
   * its selectable groups and said "6 products selected · $458"; the results
   * page counted every group it had drawn and said "8 products · $458" for the
   * identical regimen — the empty TONER slot and the sold-out eye cream made up
   * the difference, and the second number contradicted its own total as well as
   * the other page. Both now call this.
   *
   * Takes GROUPS, not steps, because the group is where the per-variant dedupe
   * has already happened and where each surface's own "is this row still on the
   * page" rules (removed, out of tier) have already been applied. A caller
   * holding only steps passes buildStepGroups(steps). */
  function buyableGroups(groups) {
    return (groups || []).filter(function (g) {
      return !!g && !!g.step && isBundleAddableStep(g.step);
    });
  }

  function countBuyableGroups(groups) {
    return buyableGroups(groups).length;
  }

  /* "6 products" / "1 product" — one spelling of the noun for both bars. */
  function productCountLabel(n) {
    return n + (n === 1 ? ' product' : ' products');
  }

  /* Keeps the FIRST step per dtcVariantId — a twice-daily product counts ONCE
   * in totals and cart quantities. */
  function dedupeByVariant(steps) {
    var seen = new Set();
    var out = [];
    for (var i = 0; i < steps.length; i++) {
      var id = steps[i].dtcVariantId;
      if (!id) { out.push(steps[i]); continue; }
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(steps[i]);
    }
    return out;
  }

  /* --- category / time labels --------------------------------------- */

  /* Category labels per slot FAMILY — the 17 families of
   * snippets/sts-steps-howto.liquid, which is the table this store's slot codes
   * are defined by. A family is the letters a slot starts with (C05 -> C,
   * CX21 -> CX, S25T -> S) and it is matched EXACTLY, never by first letter:
   * first-letter matching labelled every CX cosmetic a Cleanser, every MK mask
   * and MX lip product a Moisturizer, every SP supplement a Sunscreen and every
   * TN toner a Treatment — on the results page, the Skin Profile and the hero.
   * The same fold also decided the refill rate a bottle was predicted at, which
   * is why snippets/sts-refill-flag.liquid stopped doing it (PR #74/#79).
   *
   * This table is a MIRROR of the one in assets/regimens.js (the legacy
   * regimens page), label for label. The two files are never loaded together —
   * regimens.js is loaded only by snippets/quiz.liquid, as a module, on a page
   * this file is not on — so they cannot share one object at runtime. Change one,
   * change the other; assets/quiz-results.js holds a third copy for the legacy
   * results page, with its own older EX / R / SPF codes.
   *
   * Ordered longest-first only so the list reads like every other table of these
   * codes; the match below does not depend on the order. */
  var CATEGORY_LABELS = [
    { family: 'HA', label: 'Hypoallergenic' },
    { family: 'RX', label: 'Prescription' },
    { family: 'CX', label: 'Cosmetics' },
    { family: 'MK', label: 'Mask' },
    { family: 'MX', label: 'Lip Care' },
    { family: 'SP', label: 'Supplements' },
    { family: 'TN', label: 'Toner' },
    { family: 'PR', label: 'Prep' },
    { family: 'PP', label: 'Post-Procedure' },
    { family: 'PD', label: 'Pads' },
    { family: 'SPF', label: 'Sunscreen' },
    { family: 'EX', label: 'Exfoliant' },
    { family: 'H', label: 'Hair' },
    { family: 'C', label: 'Cleanser' },
    { family: 'E', label: 'Eye Cream' },
    { family: 'M', label: 'Moisturizer' },
    { family: 'S', label: 'Sunscreen' },
    { family: 'T', label: 'Treatment' },
    { family: 'B', label: 'Body' }
  ];

  /* The slot's FAMILY: its leading run of letters, and nothing else.
   *   C05 -> C      CX21 -> CX    S25T -> S     SP01 -> SP
   *   SPF30 -> SPF  E02 -> E      EX14 -> EX    C-05 -> C     MASK -> MASK
   * This is the same rule the two-step match below arrives at — the letters
   * before the first digit, and where there is no digit, simply the letters —
   * expressed once so a caller that needs to ASK which family a slot is in does
   * not re-derive it and get `charAt(0)` instead. That shortcut is exactly what
   * PR #88 removed: it reads SP (Supplements) as S (Sunscreen), EX (Exfoliant)
   * as E (Eye Cream), CX as C and MX as M.
   * Returns '' for a slot with no leading letter, which matches nothing.
   * ECS (or a bare E) is the regimen templates' eye PLACEHOLDER, "Eye Cream
   * Specific", which the engine resolves to E01-E05 from the quiz eye answer.
   * One that arrives unresolved is the eye cream step: family E, category Eye
   * Cream, and any E slot stands for it (sameSlot). Its label never prints -
   * assets/sts-state.js STS.stepLabel is the rule for labels. */
  var EYE_PLACEHOLDER = /^E(CS)?$/;

  function slotFamily(slot) {
    var s = String(slot === null || slot === undefined ? '' : slot).trim().toUpperCase();
    var m = /^([A-Z]+)/.exec(s);
    return EYE_PLACEHOLDER.test(s) ? 'E' : m ? m[1] : '';
  }

  function sameSlot(a, b) {
    var x = String(a).trim().toUpperCase(), y = String(b).trim().toUpperCase();
    return x === y || ((EYE_PLACEHOLDER.test(x) || EYE_PLACEHOLDER.test(y)) &&
      slotFamily(x) === 'E' && slotFamily(y) === 'E');
  }

  /* The same two-step match as assets/sts-steps.js:
   * 1. the exact family — the letters before the first digit (C05 -> C,
   *    CX21 -> CX, S25T -> S);
   * 2. a prefix fallback for a slot that is not letters-then-digits ("C-05").
   *    A prefix only counts where the LETTERS end, so an unlisted family never
   *    falls through to a single letter: CZ21 is not a Cleanser, MASK is not a
   *    Moisturizer.
   * Anything else is null, and every caller falls back to the step's own title
   * rather than printing a wrong category. */
  function categoryForSlot(slot) {
    if (!slot) return null;
    var s = String(slot).trim().toUpperCase();
    var i;
    if (EYE_PLACEHOLDER.test(s)) s = 'E';  // the eye placeholder (above)
    var fam = /^([A-Z]+)\d/.exec(s);
    if (fam) {
      for (i = 0; i < CATEGORY_LABELS.length; i++) {
        if (CATEGORY_LABELS[i].family === fam[1]) return CATEGORY_LABELS[i].label;
      }
    }
    for (i = 0; i < CATEGORY_LABELS.length; i++) {
      var p = CATEGORY_LABELS[i].family;
      if (s.indexOf(p) === 0 && !/[A-Z]/.test(s.charAt(p.length))) return CATEGORY_LABELS[i].label;
    }
    return null;
  }

  function timeBadgeLabel(time) {
    if (time === 'AM') return 'AM';
    if (time === 'PM') return 'PM';
    if (time === 'BOTH') return 'AM + PM';
    return null;
  }

  /* The eight Baumann axis words. Spec 05 §8 settles the spelling war:
   * `Non-pigmented` and `Wrinkle-prone` win over the results page's
   * `Non-Pigmented` / `Wrinkle-Prone` (sts-liquid-quiz-results.js:352-355).
   * snippets/sts-skin-types.liquid is the Liquid-side copy of the same table;
   * they are checked against each other by eye, not by code, because the
   * snippet cannot be read from JavaScript. */
  var AXIS_WORDS = {
    D: 'Dry', O: 'Oily', S: 'Sensitive', R: 'Resistant',
    P: 'Pigmented', N: 'Non-pigmented', W: 'Wrinkle-prone', T: 'Tight'
  };

  var TYPE_CODES = ['DSPT', 'DSNT', 'DSPW', 'DSNW', 'OSPT', 'OSNT', 'OSPW', 'OSNW',
    'ORPT', 'ORNT', 'ORPW', 'ORNW', 'DRPT', 'DRNT', 'DRPW', 'DRNW'];

  function typeNumber(code) {
    var i = TYPE_CODES.indexOf(String(code || '').toUpperCase());
    return i === -1 ? null : i + 1;
  }

  function axisWordsFor(code) {
    var c = String(code || '').toUpperCase();
    if (typeNumber(c) === null) return [];
    return c.split('').map(function (l) { return AXIS_WORDS[l]; });
  }

  NS.core = {
    isAddableStep: isAddableStep,
    isInformationalStep: isInformationalStep,
    isBundleAddableStep: isBundleAddableStep,
    isValidSwapCandidate: isValidSwapCandidate,
    stepFieldsFromCandidate: stepFieldsFromCandidate,
    applySwap: applySwap,
    applySwaps: applySwaps,
    substituteOutOfStock: substituteOutOfStock,
    computeAddableTotal: computeAddableTotal,
    combineTime: combineTime,
    groupKeyFor: groupKeyFor,
    buildStepGroups: buildStepGroups,
    buyableGroups: buyableGroups,
    countBuyableGroups: countBuyableGroups,
    productCountLabel: productCountLabel,
    dedupeByVariant: dedupeByVariant,
    categoryForSlot: categoryForSlot,
    slotFamily: slotFamily,
    sameSlot: sameSlot,
    timeBadgeLabel: timeBadgeLabel,
    axisWordsFor: axisWordsFor,
    typeNumber: typeNumber,
    AXIS_WORDS: AXIS_WORDS,
    TYPE_CODES: TYPE_CODES
  };

  /* ================================================================== */
  /* money — the SYMBOL has to follow the shopper's market too            */
  /* ================================================================== */

  /* SHOP-7 (results page): amounts are presentment values from Shopify, so a
   * corrected number rendered as "$202" when the charge is £202 is still a lie.
   * USD output is byte-identical to a hardcoded '$' + toFixed(2), so nothing
   * changes for the US store. */
  var currency = null;

  /* A valid ISO code pins the currency; anything else CLEARS it back to the
   * Shopify global. That mirrors the results page, whose own S.currency ('' on
   * a re-init without one) is simply handed to this setter. */
  function setCurrency(code) {
    currency = (typeof code === 'string' && code.length === 3) ? code.toUpperCase() : null;
  }

  function currencyCode() {
    if (currency) return currency;
    try {
      var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return (typeof c === 'string' && c.length === 3) ? c : 'USD';
    } catch (e) { return 'USD'; }
  }

  /* The format is the SHOP's for the shopper's market (v6.1 13.3), through the
   * one formatter in the head, STS.money (snippets/sts-money-js.liquid): the
   * same separators, symbol and code rule as the Liquid prices beside these
   * totals, whoever's browser is looking. House style since launch: a whole
   * amount in the shop's own currency prints without its ".00" ("$38", not
   * "$38.00", with grouping: "$1,299"); every other market reads the code-named
   * form Liquid prints ("$303.00 CAD"). */
  function money(n) {
    var M = window.STS && window.STS.money;
    if (M) return M.format(n, { cur: currencyCode(), trim: true });
    var amount = Number(n);
    return '$' + (isFinite(amount) ? amount : 0).toFixed(2).replace(/\.00$/, '');
  }

  function priceText(p) { return (p === null || p === undefined) ? '—' : money(p); }

  NS.money = {
    set: setCurrency, code: currencyCode, format: money, text: priceText,
    cents: function (c) { return money(c / 100); }
  };

  /* ================================================================== */
  /* plans — Subscribe & Save, read from /products/{handle}.js, never guessed */
  /* ================================================================== */

  /* Normalise a selling-plan cadence to DAYS. Cadence used to be matched by
   * comparing plan NAME strings; vendors name the same interval differently
   * ("Delivery every 30 days" vs "Delivery every 1 month"), so a shopper who
   * picked one rhythm silently got another on half the routine. Comparing
   * intervals instead of prose is the only way that cannot drift.
   * Returns null when nothing parses, so callers skip rather than guess. */
  function cadenceDays(name, optionValue) {
    var src = String(optionValue || '') + ' ' + String(name || '');
    var m = src.match(/(\d+)\s*(day|days|week|weeks|month|months|year|years)/i);
    if (!m) return null;
    var n = parseInt(m[1], 10);
    if (!isFinite(n) || n <= 0) return null;
    var unit = m[2].toLowerCase();
    if (unit.indexOf('day') === 0) return n;
    if (unit.indexOf('week') === 0) return n * 7;
    if (unit.indexOf('month') === 0) return n * 30;
    return n * 365;
  }

  function sameCadence(a, b) {
    if (a === null || b === null || a === undefined || b === undefined) return false;
    return Math.abs(a - b) <= 3;
  }

  /* DAYS is the canonical unit deliberately: it is the string Shopify prints on
   * each checkout line, because checkout renders the selling plan's own name
   * and we cannot override it. */
  function cadenceLabel(days) {
    if (days === null || days === undefined) return '';
    return 'Delivery every ' + days + (days === 1 ? ' day' : ' days');
  }

  /* The FRONT-FACING cadence reads "Monthly" / "Bimonthly". Only the two
   * marketed rhythms get a word; any other interval keeps its honest day label
   * so we never imply a rhythm a plan does not have. */
  function cadenceMarketingName(days) {
    if (days === null || days === undefined) return null;
    if (days >= 28 && days <= 31) return 'Monthly';
    if (days >= 56 && days <= 62) return 'Bimonthly';
    return null;
  }

  function cadenceShopperLabel(days, compact) {
    var word = cadenceMarketingName(days);
    if (word) return word;
    var label = cadenceLabel(days);
    return compact ? label.replace(/^Delivery every /i, 'Every ') : label;
  }

  /* Subscribe & Save is a 15%-off-every-refill offer. A handful of products
   * carry a thinner plan (Plated Skin Science: 5%, intended by the brand) —
   * those are NOT switched on by default and do not count towards the master
   * toggle or the headline figure; the shopper can still opt one in from its
   * own row. Threshold with slack so 14.9% rounding never demotes a real 15%. */
  var DEFAULT_SUB_MIN_SAVING = 0.145;

  /* Owns one product-JSON cache. The results page and the Skin Profile each
   * build their own instance — a shared singleton would make one page's render
   * scheduling fire on the other's fetches.
   *
   * onChange(handle, status) — status is 'done' or 'error'. The results page
   * acts only on 'done' (a failed fetch leaves its page exactly as it was);
   * the Skin Profile ignores the argument and re-renders either way. */
  function PlanStore(opts) {
    opts = opts || {};
    this.cache = new Map();
    this.onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    /* Called with every raw Shopify variant object as product JSON lands. The
     * response IS market-aware, so `v.price` is what the shopper will actually
     * be charged; throwing it away and rendering the USD figure from
     * cached_products is what made the quiz misquote by CA +54% / AU +57%. */
    this.onVariant = typeof opts.onVariant === 'function' ? opts.onVariant : function () {};
    /* v6.1 §6: (unit) -> true when one bottle serves the morning AND the
       evening step, which halves its days of supply. */
    this.shared = typeof opts.shared === 'function' ? opts.shared : function () { return false; };
    this.settleChecks = [];
  }

  /* Each answer, good or bad, may be the last one a whenSettled() is waiting on. */
  PlanStore.prototype.announce = function (handle, status) {
    this.onChange(handle, status);
    this.settleChecks.slice().forEach(function (check) { check(); });
  };

  /* Days of supply per variant, {id: [days, size]}, from sections/api-sts-supply
     rendered on the product's own URL (the numbers the cart row prints). A
     failed read is "no estimate", never an error. */
  function supplyOf(handle) {
    return netFetch('/products/' + encodeURIComponent(handle) + '?section_id=api-sts-supply')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var m = /<script[^>]*data-sts-supply[^>]*>([\s\S]*?)<\/script>/.exec(html);
        return m ? JSON.parse(m[1]) : {};
      })
      .catch(function () { return {}; });
  }

  PlanStore.prototype.ensure = function (step) {
    if (!step || !step.handle || !step.dtcVariantId) return;
    var handle = String(step.handle).replace(/^\/+/, '');
    if (this.cache.has(handle)) return;
    var self = this;
    var supply = supplyOf(handle);
    this.cache.set(handle, { status: 'loading', byVariant: {} });
    netFetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('product json ' + r.status); return r.json(); })
      .then(function (product) { return supply.then(function (s) { product.stsSupply = s; return product; }); })
      .then(function (product) {
        var nameById = {};
        var optionsById = {};
        (product.selling_plan_groups || []).forEach(function (g) {
          (g.selling_plans || []).forEach(function (p) {
            if (p && p.id !== null && p.id !== undefined && p.name) {
              nameById[String(p.id)] = String(p.name);
              /* `options` carries the vendor's own cadence value ("30 Days",
                 "1 Month"); it parses more reliably than the display name. */
              optionsById[String(p.id)] = (p.options || [])
                .map(function (o) { return o && o.value; })
                .filter(Boolean)
                .join(' ');
            }
          });
        });
        var byVariant = {};
        (product.variants || []).forEach(function (v) {
          var opts2 = [];
          (v.selling_plan_allocations || []).forEach(function (a) {
            var planId = a.selling_plan_id;
            var name = nameById[String(planId)];
            if (!name) return; // allocation without a named plan — dropped, never guessed
            var priceCents = (a.per_delivery_price !== null && a.per_delivery_price !== undefined)
              ? a.per_delivery_price
              : (a.price !== null && a.price !== undefined ? a.price : null);
            opts2.push({
              id: Number(planId),
              name: name,
              priceCents: priceCents,
              /* The variant's ONE-TIME price out of the same response, in the
               * same currency as priceCents. Every subscribe percentage and
               * every "you save" figure is computed against this, never against
               * step.priceMsrp: priceMsrp starts life as the regimen API's USD
               * snapshot and is only corrected once the Storefront call lands
               * (and not at all when it fails). Subtracting a market plan price
               * from a USD one-time price is how a GB routine advertised "save
               * 29%" on a 15% plan, and how CA hid the offer entirely. */
              unitCents: (typeof v.price === 'number' && isFinite(v.price)) ? v.price : null,
              intervalDays: cadenceDays(name, optionsById[String(planId)])
            });
          });
          byVariant[String(v.id)] = opts2;
          self.onVariant(v);
        });
        self.cache.set(handle, { status: 'done', byVariant: byVariant, supply: product.stsSupply || {} });
        self.announce(handle, 'done');
      })
      .catch(function () {
        // honest failure: no plans shown for this product (the toggle stays hidden)
        self.cache.set(handle, { status: 'error', byVariant: {} });
        self.announce(handle, 'error');
      });
  };

  /* For a click that builds cart lines: plans arrive up to a second or so after
   * the routine is on screen, and until then nothing is subscribed, so an add
   * built early is all one-time. Resolves true once every unit's plans have
   * landed or failed, false if the cap ran out first; never rejects. The cap
   * sits just past netFetch's own deadline, so only a stalled body reaches it. */
  var PLAN_SETTLE_MS = 9000;

  PlanStore.prototype.whenSettled = function (units, ms) {
    var self = this;
    return new Promise(function (resolve) {
      var timer = null;
      function end(settled) {
        window.clearTimeout(timer);
        var i = self.settleChecks.indexOf(check);
        if (i > -1) self.settleChecks.splice(i, 1);
        resolve(settled);
      }
      function check() { if (!self.anyLoading(units)) end(true); }
      self.settleChecks.push(check);
      timer = window.setTimeout(function () { end(false); }, ms || PLAN_SETTLE_MS);
      units.forEach(function (u) { if (isBundleAddableStep(u)) self.ensure(u); });
      check();
    });
  };

  /* [] = definitively none; null = unknown yet (loading / pending fetch) */
  PlanStore.prototype.forStep = function (step) {
    if (!step || !step.handle || !step.dtcVariantId) return [];
    var entry = this.cache.get(String(step.handle).replace(/^\/+/, ''));
    if (!entry) return null;
    if (entry.status === 'loading') return null;
    return entry.byVariant[String(step.dtcVariantId)] || [];
  };

  /* The cadences EVERY subscribable unit can honour, shortest first. One source
   * for both the cadence chips and the default picked when the toggle goes on —
   * deriving them separately is exactly how a cart ended up mixing 30-day and
   * 60-day plans. */
  PlanStore.prototype.commonCadences = function (bearing) {
    if (!bearing || !bearing.length) return [];
    var self = this;
    var out = [];
    (this.forStep(bearing[0]) || []).forEach(function (o) {
      if (o.intervalDays === null || o.intervalDays === undefined) return;
      if (out.some(function (d) { return sameCadence(d, o.intervalDays); })) return;
      var everyoneHasIt = bearing.every(function (u) {
        return (self.forStep(u) || []).some(function (p) { return sameCadence(p.intervalDays, o.intervalDays); });
      });
      if (everyoneHasIt) out.push(o.intervalDays);
    });
    out.sort(function (a, b) { return a - b; });
    return out;
  };

  /* Cheapest plan for a unit — the one the headline percentage is computed
   * from. Never a literal: decision 06-Q6 forbids printing a fixed number. */
  PlanStore.prototype.bestPlanFor = function (u) {
    var best = null;
    (this.forStep(u) || []).forEach(function (o) {
      if (o.priceCents !== null && o.priceCents !== undefined && (!best || o.priceCents < best.priceCents)) best = o;
    });
    return best;
  };

  /* The one-time price a plan price may be compared against, in the PLAN's own
   * currency. `unitCents` came out of the same /products/{handle}.js response
   * as priceCents, so the pair is always in one currency; step.priceMsrp is
   * only safe once presentment reconciliation has overwritten it, which is not
   * guaranteed to have happened (or to have succeeded) by the time a
   * percentage is drawn. Returns null when neither is usable — the caller then
   * omits the figure rather than subtracting two currencies. */
  function oneTimeFor(u, plan) {
    if (plan && plan.unitCents !== null && plan.unitCents !== undefined && isFinite(plan.unitCents)) {
      return plan.unitCents / 100;
    }
    var p = u && u.priceMsrp;
    return (typeof p === 'number' && isFinite(p)) ? p : null;
  }

  PlanStore.prototype.oneTimeFor = function (u) {
    return oneTimeFor(u, this.bestPlanFor(u));
  };

  /* One product's own Subscribe & save rate, from its cheapest real plan: the
   * number its product page prints. null when it has no plan or no usable
   * one-time price. STS-V7-GAPS-1019: a 5% product sat under a "15%" headline
   * with no rate of its own anywhere on the card. */
  PlanStore.prototype.unitPct = function (u) {
    var best = this.bestPlanFor(u);
    if (!best || best.priceCents === null || best.priceCents === undefined) return null;
    var base = oneTimeFor(u, best);
    if (base === null || !(base > 0) || best.priceCents / 100 >= base) return null;
    return Math.round(((base - best.priceCents / 100) / base) * 100);
  };

  PlanStore.prototype.isThinPlanUnit = function (u) {
    var best = this.bestPlanFor(u);
    if (!best || best.priceCents === null || best.priceCents === undefined) return false;
    var base = oneTimeFor(u, best);
    if (base === null || !(base > 0)) return false;
    return (base - best.priceCents / 100) / base < DEFAULT_SUB_MIN_SAVING;
  };

  PlanStore.prototype.planBearingUnits = function (units) {
    var self = this;
    return units.filter(function (u) {
      if (!isBundleAddableStep(u)) return false;
      var opts = self.forStep(u);
      return !!(opts && opts.length);
    });
  };

  /* Plan-bearing units the default and the master toggle act on. A thin-plan
   * product is NEVER in this set ("not ever as default", Luis 2026-08-18). */
  PlanStore.prototype.defaultSubUnits = function (units) {
    var self = this;
    return this.planBearingUnits(units).filter(function (u) { return !self.isThinPlanUnit(u); });
  };

  PlanStore.prototype.anyLoading = function (units) {
    var self = this;
    return units.some(function (u) { return isBundleAddableStep(u) && self.forStep(u) === null; });
  };

  /* Nearest plan to a target interval. Ties break toward the plan whose vendor
   * name already matches the canonical label: a product can carry two plans for
   * the SAME rhythm under different vocabulary (Zerafite Wrinkle Defense is
   * enrolled in both "every 1 month" and "every 30 days"), and without the
   * tie-break the winner was whichever Shopify happened to list first. */
  function planNearest(opts, days) {
    var best = null, bestDelta = Infinity, bestCanonical = false;
    (opts || []).forEach(function (o) {
      if (o.intervalDays === null || o.intervalDays === undefined) return;
      var delta = Math.abs(o.intervalDays - days);
      var canonical = String(o.name || '').toLowerCase() === cadenceLabel(o.intervalDays).toLowerCase();
      if (delta < bestDelta || (delta === bestDelta && canonical && !bestCanonical)) {
        bestDelta = delta; best = o; bestCanonical = canonical;
      }
    });
    return best;
  }

  /* v6.1 §6: the default schedule is how long the product lasts - its days of
   * supply, halved when one bottle is used morning and night, 60 without an
   * estimate - so each product ships on its own rhythm (Recharge still merges
   * charges that fall on the same date into one box). */
  PlanStore.prototype.supplyDays = function (u) {
    var entry = u && u.handle ? this.cache.get(String(u.handle).replace(/^\/+/, '')) : null;
    var row = entry && entry.supply ? entry.supply[String(u.dtcVariantId)] : null;
    var days = row && row[0] > 0 ? row[0] : 0;
    if (days && this.shared(u)) days = Math.round(days / 2);
    return days || 60;
  };

  /* `targetDays` given: every unit on the plan nearest it (the visitor picked
   * one rhythm). Null: each unit on the plan nearest its own days of supply.
   * `subs` is a Map(variantId -> chosen plan) the caller owns; an entry
   * already there is the visitor's own choice and is kept. */
  PlanStore.prototype.subscribeAll = function (bearing, subs, targetDays) {
    var self = this;
    var given = !(targetDays === null || targetDays === undefined);
    bearing.forEach(function (u) {
      var key = String(u.dtcVariantId);
      if (subs.has(key)) return;
      var opts = self.forStep(u) || [];
      if (!opts.length) return;
      var chosen = planNearest(opts, given ? targetDays : self.supplyDays(u)) || opts[0];
      subs.set(key, {
        planId: chosen.id, planName: chosen.name,
        priceCents: chosen.priceCents, unitCents: chosen.unitCents,
        intervalDays: chosen.intervalDays
      });
    });
  };

  /* NEW for the Skin Profile (spec §5): what a SUBSET saves. Acceptance 22
   * makes the "You save {amount}" figure and the enabled button quote the same
   * basket, so this takes the units, never "the routine". */
  PlanStore.prototype.savingsFor = function (units, subs) {
    var self = this;
    var msrp = 0, now = 0;
    units.forEach(function (u) {
      if (!isBundleAddableStep(u)) return;
      var sub = subs.get(String(u.dtcVariantId));
      /* One currency per line: a subscribed unit's "was" figure is the one-time
         price from the same payload as its plan price, never the USD snapshot
         the plan price would then be subtracted from. */
      var base = oneTimeFor(u, sub || self.bestPlanFor(u));
      if (base === null) return;
      var q = qtyOf(u);
      msrp += base * q;
      now += q * ((sub && sub.priceCents !== null && sub.priceCents !== undefined)
        ? sub.priceCents / 100 : base);
    });
    return { msrp: msrp, now: now, savings: msrp - now };
  };

  /* Headline percentage, computed from real allocations across the units that
   * carry a real plan. Returns null when nothing can be computed — the caller
   * then hides the headline rather than printing a number nobody earned. */
  PlanStore.prototype.headlinePct = function (units) {
    var self = this;
    var eligible = this.defaultSubUnits(units);
    var msrp = 0, best = 0;
    eligible.forEach(function (u) {
      var plan = self.bestPlanFor(u);
      if (!plan || plan.priceCents === null || plan.priceCents === undefined) return;
      var base = oneTimeFor(u, plan);
      if (base === null) return;
      msrp += base;
      best += plan.priceCents / 100;
    });
    if (!(msrp > 0) || !(best > 0) || best >= msrp) return null;
    return Math.round(((msrp - best) / msrp) * 100);
  };

  NS.plans = {
    Store: PlanStore,
    cadenceDays: cadenceDays,
    sameCadence: sameCadence,
    cadenceLabel: cadenceLabel,
    cadenceMarketingName: cadenceMarketingName,
    cadenceShopperLabel: cadenceShopperLabel,
    planNearest: planNearest,
    oneTimeFor: oneTimeFor,
    DEFAULT_SUB_MIN_SAVING: DEFAULT_SUB_MIN_SAVING
  };

  /* ================================================================== */
  /* cart                                                                */
  /* ================================================================== */

  /* window.theme.routes.cart first: layout/theme.liquid prints it from
     `routes.cart_url`, so it carries the market/locale prefix, and it is the
     route the live results page has always used for add.js and the settle
     poll. */
  function cartRoute() {
    try {
      var t = window.theme && window.theme.routes && window.theme.routes.cart;
      if (typeof t === 'string' && t) return t.replace(/\/+$/, '');
      var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.cart;
      if (typeof r === 'string' && r) return r.replace(/\/+$/, '');
    } catch (e) { /* fall through */ }
    return '/cart';
  }

  /* Dedupe key is (variantId, selling_plan) so a one-time line and a
   * subscription line of the same product are not collapsed into one. */
  function buildCartItems(units, subs) {
    var items = [];
    var seen = new Set();
    units.forEach(function (u) {
      if (!isBundleAddableStep(u)) return;
      var id = Number(u.dtcVariantId);
      if (!isFinite(id) || id <= 0) return;
      var sub = subs && subs.get ? subs.get(String(u.dtcVariantId)) : null;
      var key = id + '::' + (sub ? sub.planId : '');
      if (seen.has(key)) return;
      seen.add(key);
      var item = { id: id, quantity: qtyOf(u) };
      /* #1054: every caller adds routine products (Skin Profile, quiz results,
         the homepage glance), so a subscribed line is a routine one. */
      if (sub) { item.selling_plan = sub.planId; item.properties = { _sts_origin: 'routine' }; }
      items.push(item);
    });
    return items;
  }

  /* /cart/add.js does NOT replace a matching line, it INCREMENTS its quantity —
   * so a shopper who browsed a product in, or who taps the row button and then
   * the sticky bar, ended up buying two of it. Dedupe is by VARIANT alone,
   * deliberately ignoring the selling plan: two lines of the same cream, one
   * one-time and one subscription, is still the same product twice to the
   * person paying. Resolves to the number of lines actually added.
   *
   * `src` names the surface the add came from and rides on every line as the
   * hidden `_sts_src` property (spec 09 §3.7), so Shopify's own add_to_cart and
   * purchase are attributed without a second event. Callers pass their own
   * (profile_selected, profile_full, profile_upsell, results_full …). Every
   * add on the results page is results_*, every one on the Skin Profile's own
   * recommendations profile_*: those prefixes are what make the order a
   * "regimen" order (a Shopify Flow on order creation adds the tag).
   * A caller that
   * passes none gets the page's: the homepage glance card is the only one
   * today, so the fallback there is `home_glance`. A line that already
   * carries `_sts_src` keeps it. */
  function sourceForPage() {
    var t = window.STS_TRACK || {};
    return t.pageType === 'index' ? 'home_glance' : 'routine';
  }

  function tagSource(items, src) {
    var value = (typeof src === 'string' && src) ? src : sourceForPage();
    return items.map(function (i) {
      var props = Object.assign({}, i.properties || {});
      if (!props._sts_src) props._sts_src = value;
      return Object.assign({}, i, { properties: props });
    });
  }

  /* A multi-line add.js is slow: on Slow 3G with a 4x CPU, seven lines took
     9.5-11 s (QA, 2026-10-20). The flat 8 s deadline aborted the wait AFTER
     Shopify had accepted the write, so the shopper read "Check your
     connection" with the whole routine already in the cart. The deadline
     now grows with the batch: one line keeps 8 s, seven get 20 s. */
  var ADD_BASE_MS = 6000;
  var ADD_PER_LINE_MS = 2000;
  var ADD_MAX_MS = 30000;

  function addTimeoutFor(lines) {
    return Math.min(ADD_MAX_MS, Math.max(NET_TIMEOUT_MS, ADD_BASE_MS + ADD_PER_LINE_MS * (lines || 1)));
  }

  /* Even 20 s can run out. A timeout or a dropped connection says nothing
     about whether the write landed — the abort only stops OUR wait — so before
     anything is called a failure the cart is read again. Two reads, a short
     gap between them, in case the write is still being applied. Resolves to
     true (every variant is now in the cart), false (the cart answered without
     them) or null (the cart could not be read either). */
  var CONFIRM_TRIES = 2;
  var CONFIRM_GAP_MS = 1500;
  var CONFIRM_READ_MS = 10000;

  function variantsIn(cart) {
    var have = {};
    ((cart && cart.items) || []).forEach(function (li) {
      if (li && li.id !== null && li.id !== undefined) have[String(li.id)] = true;
    });
    return have;
  }

  function confirmLanded(items, readFn) {
    var read = readFn || function () {
      return netFetch(cartRoute() + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }, CONFIRM_READ_MS)
        .then(function (r) { return r.ok ? r.json() : null; });
    };
    var answered = false;
    function attempt(n) {
      return read().then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return null;
        answered = true;
        var have = variantsIn(cart);
        return items.every(function (i) { return have[String(i.id)]; }) ? true : null;
      }, function () { return null; }).then(function (ok) {
        if (ok) return true;
        if (n + 1 >= CONFIRM_TRIES) return answered ? false : null;
        return new Promise(function (resolve) { setTimeout(resolve, CONFIRM_GAP_MS); })
          .then(function () { return attempt(n + 1); });
      });
    }
    return attempt(0);
  }

  /* Variants whose add went out and was never confirmed either way: the cart
     could not be read after the failure. A later add that also cannot read the
     cart must not post them blind — that is how a retry doubles a routine. */
  var unconfirmed = {};

  function ambiguous(err) {
    /* Shopify answered (422 etc.): the write was refused, nothing to check. */
    return !(err && err.shopifyAnswered);
  }

  function slowError() {
    var e = new Error('timeout');
    e.description = 'Your connection is slow and we could not confirm your cart. Tap the button again - nothing will be added twice.';
    return e;
  }

  /* A visitor's FIRST cart write is what creates their cart: GET /cart.js
     hands out a throwaway token and sets no `cart` cookie; the cookie comes
     back on the write's answer. When that first write is a slow routine add
     and we stop waiting for it, the browser never receives the cookie, so the
     re-read above looks at a new, empty cart and calls a landed add a failure
     (QA, WebKit, 2026-10-20). An empty /cart/update.js first creates the cart
     and its cookie in a small, quick request. Never blocks the add. */
  function hasCartCookie() {
    try { return /(?:^|;\s*)cart=/.test(document.cookie || ''); } catch (e) { return true; }
  }

  function ensureCart() {
    if (hasCartCookie()) return Promise.resolve();
    return netFetch(cartRoute() + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}'
    }).then(function () {}, function () {});
  }

  function addItems(items, src) {
    if (!items || !items.length) return Promise.resolve(0);
    items = tagSource(items, src);
    var cartRead = true;
    return netFetch(cartRoute() + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { cartRead = false; return { items: [] }; })  // cart unreadable: add everything
      .then(function (cart) {
        if (cartRead) unconfirmed = {};
        var already = variantsIn(cart);
        var toAdd = items.filter(function (i) { return !already[String(i.id)]; });
        if (!toAdd.length) return 0;
        /* ...unless an earlier add of these may already be in it. */
        if (!cartRead && toAdd.some(function (i) { return unconfirmed[String(i.id)]; })) throw slowError();
        return ensureCart().then(function () { return toAdd; });
      })
      .then(function (toAdd) {
        if (!toAdd) return 0;
        return netFetch(cartRoute() + '/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd })
        }, addTimeoutFor(toAdd.length)).then(function (r) {
          if (!r.ok) {
            return r.json().then(
              function (body) {
                var e = new Error((body && (body.description || body.message)) || 'Cart add failed (' + r.status + ')');
                e.description = body && body.description;  // STS.shopperError prints this, never the message
                e.shopifyAnswered = r.status < 500;
                throw e;
              },
              function () { throw new Error('Cart add failed (' + r.status + ')'); }
            );
          }
          /* The write is done once the status is in; a body that times out
             on the way down is not a failed add. */
          return r.json().then(function () { return toAdd.length; }, function () { return toAdd.length; });
        }).catch(function (err) {
          if (!ambiguous(err)) throw err;
          return confirmLanded(toAdd).then(function (landed) {
            if (landed) return toAdd.length;  // it worked; the wait was what failed
            if (landed === null) {
              toAdd.forEach(function (i) { unconfirmed[String(i.id)] = true; });
              throw slowError();
            }
            throw err;  // the cart answered without them: a plain retry is safe
          });
        });
      });
  }

  /* --- discount codes: ADD to the cart's list, never replace it ------------
   *
   * SUBSCRIBE10 is public in the page source and that is fine: it is Purchase
   * type = Both, minimum 3 items, one use per customer, and Shopify enforces
   * every one of those rules server side. Cart API, not the /discount/ link:
   * /discount/A,B stores the literal "A,B" as ONE invalid code and checkout
   * then drops it — and SUBSCRIBE10 with it.
   *
   * `discount` on /cart/update.js SETS the cart's whole code list; it does not
   * add to it. Sending only our codes silently wiped a gift, affiliate or promo
   * code the customer had already entered, and they found out at checkout or
   * never. So every write here goes:
   *
   *   1. GET /cart.js — discount_codes is what is on the cart right now.
   *   2. Send the UNION: every code already there, spelled exactly as it is,
   *      plus each of ours that is not there yet. Codes are case-insensitive;
   *      none is sent twice.
   *   3. Judge the answer. Shopify reports `applicable` per code, and when two
   *      codes do not stack it keeps whichever is worth more to the buyer, in
   *      either order (this store, 2026-09-18: ROUTINE25 + SUBSCRIBE10 ->
   *      ROUTINE25 applicable, SUBSCRIBE10 not, both ways round). So one of
   *      ours can WIN and knock the customer's code out. Here the customer's
   *      code wins instead:
   *        - a customer code that applied before our write and does not after
   *          it was displaced by one of ours;
   *        - one of ours that does not apply while a customer code does was
   *          refused next to it.
   *      Either way ours comes off, the customer's list is written back as it
   *      was, and the shopper is told why (showCodesNotice).
   *
   * "The customer's" = any code on the cart that this call was not asked to
   * apply: typed by hand, arrived on a /discount/ link, or left by another
   * page. It is never taken off the list, applicable or not.
   *
   * Known limit: the cart does not say WHY a code is refused. SUBSCRIBE10 on a
   * cart under 3 items, or for a customer who has already used it, is refused
   * on its own too; next to a customer's code that looks the same as a
   * stacking refusal, so it comes off with the same notice. It could not have
   * applied in either case.
   *
   * Reads the ROOT '/cart.js' and posts to the ROOT '/cart/update.js', not
   * cartRoute(): that exact call is the one verified on this store
   * (2026-08-18) and the one the live results page makes.
   *
   * Resolves to the codes the cart reports as applicable (the customer's
   * included; ours spelled as the caller spelled them). Resolves to null ONLY
   * when the cart API refused and the cart held no code of the customer's:
   * the results page falls back to GET /discount/SUBSCRIBE10 on null, and that
   * link REPLACES the cart's codes, so it must never fire over one of theirs.
   * Never rejects. */
  var CODES_NOTICE_HOLD_MS = 3000;  // how long the caller is held so it can be read

  function codeKey(code) { return String(code).trim().toUpperCase(); }

  function cartCodeList(cart) {
    var list = cart && cart.discount_codes;
    return Array.isArray(list) ? list.filter(function (d) { return d && d.code; }) : null;
  }

  function cartCodeStatus(cart) {
    var status = {};
    (cartCodeList(cart) || []).forEach(function (d) { status[codeKey(d.code)] = !!d.applicable; });
    return status;
  }

  /* A cart without a discount_codes list is treated as no answer at all. */
  function readCartCodes() {
    return netFetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { return cartCodeList(cart) ? cart : null; })
      .catch(function () { return null; });
  }

  function postCartUpdate(body) {
    return netFetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (body && body.discount !== undefined) codesWrittenAt = Date.now();
        return r.ok ? r.json() : null;
      })
      .then(function (cart) { return cartCodeList(cart) ? cart : null; })
      .catch(function () { return null; });
  }

  /* Write cart attributes that differ from what the cart holds. The quiz
     results page calls it with stq_session as soon as results show, so ANY
     later checkout or order from this cart — whatever page it was added on —
     is linked to the quiz (checkouts/orders webhooks → quiz_widget_checkouts /
     _orders). stq_stamped says it came from here, not from a routine add, so
     Past routines does not list every order from that cart as a routine
     (snippets/sts-past-routines-body.liquid). Queued with the other attribute
     writers (snippets/sts-cart-attr-writes.liquid). Resolves {ok, wrote}; never
     rejects, never blocks the page. */
  function stampCart(attributes) {
    function write() {
      return readCart().then(function (cart) {
        var have = (cart && cart.attributes) || {}, diff = null;
        Object.keys(attributes || {}).forEach(function (k) {
          if (attributes[k] && have[k] !== attributes[k]) { diff = diff || {}; diff[k] = attributes[k]; }
        });
        if (!diff) return { ok: !!cart, wrote: false };
        return postCartUpdate({ attributes: diff }).then(function (after) { return { ok: !!after, wrote: true }; });
      });
    }
    var q = window.STSCart;
    return q && q.exclusive ? q.exclusive(write) : write();
  }

  function applyDiscountCodes(codes, attributes) {
    var ours = [];
    var oursByKey = {};
    (codes || []).forEach(function (c) {
      var s = c ? String(c).trim() : '';
      if (!s || oursByKey[codeKey(s)]) return;
      oursByKey[codeKey(s)] = s;
      ours.push(s);
    });
    if (!ours.length) return Promise.resolve(null);
    var pendingAttributes = attributes || null;
    var customerSeen = false; // once true, never answer null (see above)

    function applicableOf(cart) {
      return (cartCodeList(cart) || [])
        .filter(function (d) { return d.applicable; })
        .map(function (d) { return oursByKey[codeKey(d.code)] || String(d.code); });
    }

    function write(list) { return postCartUpdate({ discount: list.join(',') }); }

    return readCartCodes()
      .then(function (cart) {
        if (cart || !pendingAttributes) return cart;
        /* /cart.js unreadable: the attribute write answers with the cart too. */
        var attrs = pendingAttributes;
        pendingAttributes = null;
        return postCartUpdate({ attributes: attrs });
      })
      .then(function (before) {
        if (!before) return null; // cart API refused: nothing was written, nothing was lost

        var existing = cartCodeList(before).map(function (d) { return String(d.code); });
        var present = {};
        existing.forEach(function (c) { present[codeKey(c)] = true; });
        var customer = existing.filter(function (c) { return !oursByKey[codeKey(c)]; });
        var statusBefore = cartCodeStatus(before);
        var liveBefore = customer.filter(function (c) { return statusBefore[codeKey(c)]; });
        var toAdd = ours.filter(function (c) { return !present[codeKey(c)]; });
        customerSeen = customer.length > 0;

        if (!toAdd.length) {
          /* Every code is already there: nothing to merge. The attributes
             still ride, without a `discount` key, which leaves codes alone. */
          if (!pendingAttributes) return applicableOf(before);
          return postCartUpdate({ attributes: pendingAttributes })
            .then(function (cart) { return applicableOf(cart || before); });
        }

        /* ok = no customer code that applied before has stopped applying, and
           none of `trial` is sitting refused next to a customer code that
           applies. */
        function verdict(cart, trial) {
          var status = cartCodeStatus(cart);
          var displaced = liveBefore.filter(function (c) { return status[codeKey(c)] !== true; });
          var customerLive = customer.filter(function (c) { return status[codeKey(c)] === true; });
          var refused = customerLive.length
            ? trial.filter(function (c) { return status[codeKey(c)] !== true; })
            : [];
          return { ok: !displaced.length && !refused.length, displaced: displaced, refused: refused };
        }

        /* Stacking was refused: take ours off until the customer's codes stand
           as they did, then say so. Resolves to the final cart. */
        function keepCustomerCodes(after, first) {
          var kept = [];
          var dropped = [];
          var onCart = existing.concat(toAdd).join(','); // what the cart holds, as far as we know
          var last = after;
          var steps = Promise.resolve();

          if (!first.displaced.length) {
            /* Nothing was knocked out: exactly the refused codes come off. */
            dropped = first.refused.slice();
            kept = toAdd.filter(function (c) { return dropped.indexOf(c) === -1; });
          } else if (toAdd.length === 1) {
            dropped = toAdd.slice();
          } else {
            /* The answer does not say WHICH of ours did the displacing: add
               them back one at a time, keeping each that leaves the customer's
               codes standing. */
            toAdd.forEach(function (code) {
              steps = steps.then(function () {
                var trial = existing.concat(kept, [code]);
                return write(trial).then(function (cart) {
                  onCart = cart ? trial.join(',') : '?';
                  if (cart) last = cart;
                  if (cart && verdict(cart, [code]).ok) kept.push(code);
                  else dropped.push(code);
                });
              });
            });
          }

          return steps
            .then(function () {
              var target = existing.concat(kept);
              if (onCart === target.join(',')) return last;
              /* One retry: until this lands, ours may still outrank theirs. */
              return write(target).then(function (cart) { return cart || write(target); });
            })
            .then(function (cart) {
              if (kept.length && cart && !verdict(cart, kept).ok) {
                /* Safety net: still wrong with some of ours on. The customer's
                   list alone. */
                dropped = dropped.concat(kept);
                kept = [];
                return write(existing);
              }
              return cart;
            })
            .then(function (cart) {
              if (!cart) return last; // final state unconfirmed: claim nothing
              var status = cartCodeStatus(cart);
              var theirs = customer.filter(function (c) { return status[codeKey(c)] === true; });
              if (!dropped.length || !theirs.length) return cart;
              var message = codesMessage(theirs.map(codeKey), dropped.map(codeKey));
              /* Deliberate hook, no listener today by design: showCodesNotice()
                 below already renders the default notice, and this event is how
                 a section opts out of it and renders the same three fields its
                 own way. Nothing here depends on anyone hearing it. */
              try {
                document.dispatchEvent(new CustomEvent('sts:cart:codes-kept', {
                  detail: { kept: theirs, dropped: dropped, message: message }
                }));
              } catch (e) { /* the notice below still shows */ }
              if (!showCodesNotice(message)) return cart;
              /* Hold the caller a moment: the results page goes straight to
                 checkout next and would take the notice with it. */
              return new Promise(function (resolve) {
                setTimeout(function () { resolve(cart); }, CODES_NOTICE_HOLD_MS);
              });
            });
        }

        var body = { discount: existing.concat(toAdd).join(',') };
        if (pendingAttributes) body.attributes = pendingAttributes;
        return postCartUpdate(body).then(function (after) {
          /* Write refused: the cart is as it was. With a customer code on it,
             answer with an array so the caller's /discount/ fallback (which
             would replace their code) does not run. */
          if (!after) return customerSeen ? applicableOf(before) : null;
          var first = verdict(after, toAdd);
          if (first.ok) return applicableOf(after);
          return keepCustomerCodes(after, first).then(applicableOf);
        });
      })
      .catch(function () { return customerSeen ? [] : null; });
  }

  /* A stale app write can land well after ours: LlamaCart's writes were seen
     taking up to 2.9 s (2026-10-21). So the check below reads the cart no
     sooner than this long after the last discount write answered. The
     cashback step before it usually uses up most of the wait. */
  var CODES_QUIET_MS = 3000;
  var codesWrittenAt = 0;

  function codesQuiet() {
    var wait = codesWrittenAt ? CODES_QUIET_MS - (Date.now() - codesWrittenAt) : 0;
    return wait > 0 ? new Promise(function (resolve) { setTimeout(resolve, wait); }) : Promise.resolve();
  }

  /* --- discount codes: still on the cart when the shopper leaves -----------
   *
   * A code the cart took can be lost again before checkout. Shopify saves the
   * WHOLE cart on every write, and the last write to land wins. LlamaCart (the
   * cart app) answers every cart POST with writes of its own: an
   * `_lcu_auto_added` attribute update, then /cart/add.js for the Skin Type
   * Cash Reward line. If one of those starts before our discount write lands
   * and finishes after it, it saves the cart it read: no codes, and no
   * stq_session either. Every later answer then says discount_codes: [].
   * Live QA, 2026-10-20/21: 5 of 20 first runs reached checkout without
   * SUBSCRIBE10 ($232.04, not $222.04). Repro'd on live 2026-10-21 with the
   * add -> auto-claim -> discount timing of a fresh quiz taker.
   *
   * So before the caller leaves: give a stale write time to land
   * (CODES_QUIET_MS), let the cart settle, read it, and if a code
   * from `codes` has gone, write the list back ONCE (with any attribute that
   * also went missing), settle and read again. Nothing can undo that second
   * write the same way: the app's reactions to it start after it lands, so
   * they read the codes.
   *
   * `codes` = the list that should be on the cart: what applyDiscountCodes()
   * resolved to, the customer's own codes included. Its stacking rules have
   * already run, so the list goes back as it is.
   *
   * Resolves to { missing, rewrote }: the codes still not on the cart
   * (all of them when the cart could not be read) and whether a write was
   * needed. Never rejects. */
  function cartCodesMissing(cart, want) {
    var have = {};
    (cartCodeList(cart) || []).forEach(function (d) { have[codeKey(d.code)] = true; });
    return want.filter(function (c) { return !have[codeKey(c)]; });
  }

  function ensureDiscountCodes(codes, attributes) {
    var want = [];
    var seen = {};
    (codes || []).forEach(function (c) {
      var s = c ? String(c).trim() : '';
      if (s && !seen[codeKey(s)]) { seen[codeKey(s)] = true; want.push(s); }
    });
    if (!want.length) return Promise.resolve({ missing: [], rewrote: false });

    /* The settle's own last read is the check: a cart without a code list
       is no answer. */
    var settled = function () {
      return settleCart().then(function (cart) { return cartCodeList(cart) ? cart : null; });
    };
    return codesQuiet()
      .then(settled)
      .then(function (cart) {
        if (!cart) return { missing: want, rewrote: false };
        var missing = cartCodesMissing(cart, want);
        var have = cart.attributes || {};
        var attrs = null;
        Object.keys(attributes || {}).forEach(function (k) {
          if (have[k] !== attributes[k]) { attrs = attrs || {}; attrs[k] = attributes[k]; }
        });
        if (!missing.length && !attrs) return { missing: [], rewrote: false };
        var body = {};
        if (missing.length) {
          body.discount = cartCodeList(cart).map(function (d) { return String(d.code); }).concat(missing).join(',');
        }
        if (attrs) body.attributes = attrs;
        return postCartUpdate(body)
          .then(function () { return missing.length ? settled() : cart; })
          .then(function (after) {
            return { missing: after ? cartCodesMissing(after, want) : want, rewrote: true };
          });
      })
      .catch(function () { return { missing: want, rewrote: false }; });
  }

  function listWords(words) {
    if (words.length < 2) return words.join('');
    return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1];
  }

  /* "Your code GIFT20 is applied. SUBSCRIBE10 can’t be combined with it." */
  function codesMessage(kept, dropped) {
    var many = kept.length > 1;
    return (many ? 'Your codes ' : 'Your code ') + listWords(kept) +
      (many ? ' are applied. ' : ' is applied. ') +
      listWords(dropped) + ' can’t be combined with ' + (many ? 'them.' : 'it.');
  }

  /* A popup, not a toast (v5.1 §1.5): a code the customer typed being kept
     over ours is something they must notice. The shared queue shows it after
     whatever is open (assets/sts-popups.js). Returns false if it could not
     show, so the caller is not held for nothing. */
  function showCodesNotice(message) {
    var P = window.STS && window.STS.popups;
    if (!P || !P.notice) return false;
    return P.notice({ id: 'sts-codes-kept', title: 'Your code is applied', body: message });
  }

  /* The free "Skin Type Cash Reward" line is added by the THEME's cashback
     logic (layout/theme.liquid), which listens for cart changes and runs on a
     throttled setTimeout(..., 500). Anything that navigates or renders before
     that timer beats it, and the reward silently never makes it onto the order.
     Call the theme's own updaters directly and wait for them. Sequential,
     because updateCartItemGCValue() reads the cart updateCartWithGCValue() just
     rewrote. Failures are swallowed: a missing reward line must never block a
     shopper. */
  function applyCashbackLines() {
    return Promise.resolve()
      .then(function () {
        return typeof window.updateCartWithGCValue === 'function' ? window.updateCartWithGCValue() : null;
      })
      .then(function () {
        return typeof window.updateCartItemGCValue === 'function' ? window.updateCartItemGCValue() : null;
      })
      .then(waitForCartSettle)
      .catch(function () { /* never block the shopper on the reward line */ });
  }

  /* theme.liquid only writes the `gc-value` cart ATTRIBUTE; the free line is
     added asynchronously by the cart app. Rather than couple to a third party's
     internals, wait for the cart to stop changing: poll until item_count, the
     total and the discount codes are identical on two consecutive reads
     (a late app write can drop the codes without touching the count; see
     ensureDiscountCodes), capped so a misbehaving app can never strand anyone
     on a spinner. */
  var CART_SETTLE_INTERVAL_MS = 300;
  var CART_SETTLE_MAX_MS = 2000;
  /* The 2s cap above is only tested BETWEEN polls, so it could never end a poll
     that was still in flight — one hung read defeated the whole cap and the
     comment about never stranding anyone was not true. Each poll now carries a
     deadline of its own, shorter than the cap it serves. */
  var CART_SETTLE_POLL_MS = 1500;

  function cartSettleKey(cart) {
    if (!cart) return 'none';
    var codes = (cartCodeList(cart) || []).map(function (d) { return codeKey(d.code) + (d.applicable ? '' : '?'); });
    return [cart.item_count, cart.total_price, codes.join(',')].join('|');
  }

  /* Resolves to the last cart it read (null if that read failed). */
  function settleCart() {
    var started = Date.now();
    var lastKey = null;
    function poll() {
      return netFetch(cartRoute() + '.js', { credentials: 'same-origin' }, CART_SETTLE_POLL_MS)
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var key = cartSettleKey(cart);
          if (lastKey !== null && key === lastKey) return cart;         // settled
          if (Date.now() - started >= CART_SETTLE_MAX_MS) return cart;  // capped
          lastKey = key;
          return new Promise(function (resolve) { setTimeout(resolve, CART_SETTLE_INTERVAL_MS); }).then(poll);
        })
        .catch(function () { return null; }); // a failed poll must not block anyone
    }
    return poll();
  }

  function waitForCartSettle() {
    return settleCart().then(function () { return null; });
  }

  function readCart() {
    return netFetch(cartRoute() + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* Tell the page its cart changed, then open the drawer.

     `sts:cart:updated` is the theme's ONE canonical "the cart was written"
     event. Every v4 file that writes the cart fires it — here, sts-product.js,
     sts-fbt.js, sts-collection.js, sts-loyalty.js and sts-liquid-quiz-results
     .js through this function — and sts-loyalty.js listens on it to hand back a
     reward entitlement whose free line the shopper has since deleted.

     `theme:cart:change` is the legacy alias and is NOT optional: assets/theme
     .js:1505/1818/2173 listen on it for the header count, the free-shipping bar
     and the drawer body, so it goes out with every canonical dispatch.

     Both carry the same `detail.cart`. `theme:drawer:open` with {reinit:true}
     on [data-drawer="drawer-cart"] is how theme.js:2190 is asked to reload the
     drawer HTML. All three are events — nothing here reaches inside the theme's
     classes. */
  var CART_UPDATED = 'sts:cart:updated';
  var CART_UPDATED_LEGACY = 'theme:cart:change';

  function notifyCartChanged(cart) {
    var detail = { cart: cart };
    try {
      document.dispatchEvent(new CustomEvent(CART_UPDATED, { detail: detail }));
      document.dispatchEvent(new CustomEvent(CART_UPDATED_LEGACY, { detail: detail }));
    } catch (e) { /* older browsers: the drawer still reloads below */ }
  }

  function openCartDrawer() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (drawer) {
      try {
        drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false, detail: { reinit: true } }));
        return true;
      } catch (e) { /* fall through to the toggle */ }
    }
    var toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
    if (toggle) { toggle.click(); return true; }
    return false;
  }

  NS.cart = {
    route: cartRoute,
    buildItems: buildCartItems,
    add: addItems,
    addTimeout: addTimeoutFor,
    ensureCart: ensureCart,
    confirmLanded: confirmLanded,
    applyDiscountCodes: applyDiscountCodes,
    ensureDiscountCodes: ensureDiscountCodes,
    stamp: stampCart,
    applyCashbackLines: applyCashbackLines,
    waitForCartSettle: waitForCartSettle,
    read: readCart,
    notifyChanged: notifyCartChanged,
    openDrawer: openCartDrawer
  };

  /* ================================================================== */
  /* drawer — candidate list, notes, tags                                */
  /* ================================================================== */

  function productHref(p) {
    if (p && p.url) {
      try { if (/^https?:$/.test(new URL(p.url, location.origin).protocol)) return p.url; } catch (e) { /* bad url */ }
    }
    if (p && p.handle) return '/products/' + String(p.handle).replace(/^\/+/, '');
    return null;
  }

  /* The options for one step, in the order they are shown.
   *
   *   current  — the step's product now (always first, marked "In use")
   *   original — the engine's own pick, when it has been swapped away from
   *   candidates — same-slot, in-stock, approved alternatives
   *
   * `taken` marks a variant already used by ANOTHER step. These used to be
   * greyed out ("Used elsewhere") — but the same cleanser morning and evening
   * is a perfectly good routine and the CHEAPER one, because totals and the
   * cart already count a twice-daily product once. So it stays selectable,
   * labelled "Use here too", and says what it saves.
   *
   * NEW for the Skin Profile (spec §5): `likedBrands` (vendor handles) sorts
   * matching options to the front, behind the current product. Passing an empty
   * list reproduces the results page's order exactly. */
  function candidateEntries(opts) {
    var current = opts.current || {};
    var orig = opts.original || null;
    var takenIds = opts.takenIds || new Set();
    var liked = (opts.likedBrands || []).map(function (b) { return String(b).toLowerCase(); });

    var list = [];
    var seen = new Set();
    function push(entry) {
      var id = entry.candidate.dtcVariantId;
      var key = id ? String(id) : 'x:' + list.length;
      if (seen.has(key)) return;
      seen.add(key);
      list.push(entry);
    }

    if (current.dtcVariantId && current.title) {
      push({ candidate: current, isCurrent: true, isOriginal: false });
    }
    if (orig && isValidSwapCandidate(orig) &&
      (!current.dtcVariantId || String(orig.dtcVariantId) !== String(current.dtcVariantId))) {
      push({ candidate: orig, isCurrent: false, isOriginal: true });
    }
    (opts.candidates || current.swapCandidates || []).forEach(function (c) {
      if (!isValidSwapCandidate(c)) return;
      if (current.dtcVariantId && String(c.dtcVariantId) === String(current.dtcVariantId)) return;
      push({ candidate: c, isCurrent: false, isOriginal: false });
    });

    var currentShared = !!(current.dtcVariantId && takenIds.has(String(current.dtcVariantId)));
    list.forEach(function (entry) {
      entry.taken = !entry.isCurrent && !!entry.candidate.dtcVariantId &&
        takenIds.has(String(entry.candidate.dtcVariantId));
      /* What choosing an already-owned bottle saves: this step's own product,
         unless that product is itself shared with another step (then nothing
         leaves the cart and the swap is cost-neutral). */
      entry.sharedSaves = entry.taken && !currentShared && current.priceMsrp ? current.priceMsrp : 0;
      entry.liked = liked.length > 0 &&
        liked.indexOf(String(entry.candidate.brand || '').toLowerCase()) !== -1;
    });

    if (liked.length) {
      /* Stable: only liked options move, and only past non-liked ones. The
         current product keeps position 0 regardless — moving it would make the
         drawer look like it had changed the routine. */
      var head = list.slice(0, 1).filter(function (e) { return e.isCurrent; });
      var rest = list.slice(head.length);
      rest = rest.map(function (e, i) { return { e: e, i: i }; })
        .sort(function (a, b) {
          if (a.e.liked !== b.e.liked) return a.e.liked ? -1 : 1;
          return a.i - b.i;
        })
        .map(function (x) { return x.e; });
      list = head.concat(rest);
    }
    return list;
  }

  function savesSuffix(entry) {
    return entry.sharedSaves ? ' · saves ' + money(entry.sharedSaves) : '';
  }

  function sharedNote(entry, period, stepNumber) {
    var saves = savesSuffix(entry);
    if (period && stepNumber) {
      return 'Already in your ' + period + ' routine, step ' + stepNumber + saves;
    }
    return 'Already in another step — one bottle covers both' + saves;
  }

  /* The results page's phone tile has room for three words, not a sentence. */
  function sharedNoteShort(entry) {
    return 'Also in routine' + savesSuffix(entry);
  }

  /* The alternative's price minus the CURRENT pick's, named as such: "Saves
   * $12" beside a card's own "50% OFF" read as two savings (demo QA,
   * 2026-09-22). One wording everywhere, so no caller adds a suffix. */
  function deltaNote(candidate, current) {
    if (!candidate || candidate.priceMsrp === null || candidate.priceMsrp === undefined) return '';
    if (!current || current.priceMsrp === null || current.priceMsrp === undefined) return '';
    return deltaWords(candidate.priceMsrp - current.priceMsrp);
  }

  function deltaWords(delta) {
    if (!delta) return 'Same price as current';
    return money(Math.abs(delta)) + (delta < 0 ? ' less' : ' more') + ' than current';
  }

  /* Swap-card tag. Every value is DERIVED from real data or omitted — never
   * invented: `sale` only when the variant has a genuine compare-at above its
   * price, `budget`/`premium` only with at least 3 options to rank.
   * "Derm pick" is deliberately absent: every product in the drawer is
   * dermatologist-approved for this type, so as a way to tell one option from
   * another it would carry no information. */
  function candidateTag(c, allCandidates) {
    if (c.compareAt && c.priceMsrp && c.compareAt > c.priceMsrp) {
      var pct = Math.round(((c.compareAt - c.priceMsrp) / c.compareAt) * 100);
      if (pct > 0) return { key: 'sale', label: pct + '% off' };
    }
    var prices = [];
    allCandidates.forEach(function (x) {
      if (typeof x.priceMsrp === 'number' && isFinite(x.priceMsrp)) prices.push(x.priceMsrp);
    });
    if (prices.length < 3 || typeof c.priceMsrp !== 'number') return null;
    var min = Math.min.apply(null, prices);
    var max = Math.max.apply(null, prices);
    if (min === max) return null;
    if (c.priceMsrp === min) return { key: 'budget', label: 'Budget pick' };
    if (c.priceMsrp === max) return { key: 'premium', label: 'Premium' };
    return null;
  }

  /* Focus trap shared by the drawer, the removal modal and any future dialog on
   * these two pages. `open` returns a close function; both are idempotent. */
  function focusablesIn(el) {
    return [].slice.call(el.querySelectorAll('button,a[href],input,select,textarea,[tabindex]'))
      .filter(function (n) { return n.tabIndex >= 0 && !n.disabled && n.getClientRects().length > 0; });
  }

  function trapFocus(el, onEscape) {
    function onKeydown(e) {
      if (el.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); if (onEscape) onEscape(); return; }
      if (e.key !== 'Tab') return;
      var f = focusablesIn(el);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    document.addEventListener('keydown', onKeydown, true);
    return function release() { document.removeEventListener('keydown', onKeydown, true); };
  }

  NS.drawer = {
    productHref: productHref,
    candidateEntries: candidateEntries,
    sharedNote: sharedNote,
    sharedNoteShort: sharedNoteShort,
    deltaNote: deltaNote,
    deltaWords: deltaWords,
    candidateTag: candidateTag,
    focusablesIn: focusablesIn,
    trapFocus: trapFocus
  };
}());
